import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Activity,
  BarChart3,
  BookOpen,
  Database,
  Download,
  RefreshCw,
  Search,
  TrendingUp,
  UserCheck,
  Users,
  Video,
} from "lucide-react";
import PersonTree from "../PersonTree";
import organizationService from "../../services/organizationService";
import { getRankColor, getRankLabel, getRankStyle } from "../../utils/rankStyles";
import {
  organizationTreeDemoAnalytics,
  organizationTreeDemoPersons,
} from "../../data/organizationTreeDemo";
import "./MyDashboard.css";

const API = process.env.REACT_APP_API_BASE;

const MyDashboard = () => {
  const adminEmail = useSelector((state) => state?.user?.user?.email);
  const [persons, setPersons] = useState([]);
  const [goals, setGoals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState("30d");
  const [activeView, setActiveView] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [demoMode, setDemoMode] = useState(false);
  const [demoPersons, setDemoPersons] = useState(organizationTreeDemoPersons);
  const [activityChartType, setActivityChartType] = useState("bars");
  const [participantSearch, setParticipantSearch] = useState("");
  const [density, setDensity] = useState("comfortable");

  const loadDashboard = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");

    try {
      const [treeResult, analyticsResult] = await Promise.allSettled([
        organizationService.getTreeData(),
        axios.get(`${API}/analytics/get`, {
          params: { timeRange, includeDetails: "true" },
          headers: {
            ...(process.env.REACT_APP_TOKEN
              ? { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` }
              : {}),
            "x-admin-email": adminEmail || "",
          },
        }),
      ]);

      const treeData = treeResult.status === "fulfilled"
        ? treeResult.value
        : { persons: [], goals: [] };
      const analyticsData = analyticsResult.status === "fulfilled"
        ? analyticsResult.value.data?.data || analyticsResult.value.data || null
        : null;

      setPersons(treeData.persons);
      setGoals(treeData.goals);
      setAnalytics(analyticsData);

      const hasRealData =
        treeData.persons.length > 0 ||
        Number(analyticsData?.basicMetrics?.totalUsers || 0) > 0;
      setDemoMode(!hasRealData);

      if (treeResult.status === "rejected" || analyticsResult.status === "rejected") {
        setError("O parte dintre datele reale nu este disponibilă. Poți folosi setul demonstrativ.");
      }
    } catch (requestError) {
      console.error("Dashboard load failed:", requestError);
      setDemoMode(true);
      setError("Datele reale nu sunt disponibile. Este afișat setul demonstrativ.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [adminEmail, timeRange]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const updatePerson = useCallback(async (updatedPerson) => {
    const sourcePersons = demoMode ? demoPersons : persons;
    const currentPerson = sourcePersons.find(
      (person) => person.documentId === updatedPerson.documentId
    );
    if (!currentPerson) return;

    const changes = ["name", "personId", "type", "assignedTo"].reduce(
      (result, key) => {
        if (updatedPerson[key] !== currentPerson[key]) {
          result[key] = updatedPerson[key];
        }
        return result;
      },
      {}
    );

    if (Object.keys(changes).length === 0) return;

    const updateLocalPersons = demoMode ? setDemoPersons : setPersons;
    updateLocalPersons((current) =>
      current.map((person) => {
        if (person.documentId === updatedPerson.documentId) {
          return { ...person, ...changes };
        }
        if (changes.name && person.assignedTo === currentPerson.name) {
          return { ...person, assignedTo: changes.name };
        }
        return person;
      })
    );

    if (demoMode) {
      toast.success("Structura demonstrativă a fost actualizată local.");
      return;
    }

    try {
      const saved = await organizationService.updatePerson(currentPerson, changes, adminEmail);
      setPersons((current) =>
        current.map((person) => {
          if (person.documentId === currentPerson.documentId) {
            return { ...person, ...saved };
          }
          if (changes.name && person.assignedTo === currentPerson.name) {
            return { ...person, assignedTo: changes.name };
          }
          return person;
        })
      );
      toast.success("Structura organizației a fost actualizată.");
    } catch (updateError) {
      setPersons((current) =>
        current.map((person) => {
          if (person.documentId === currentPerson.documentId) return currentPerson;
          if (changes.name && person.assignedTo === changes.name) {
            return { ...person, assignedTo: currentPerson.name };
          }
          return person;
        })
      );
      toast.error(updateError?.response?.data?.error?.message || updateError.message);
      throw updateError;
    }
  }, [adminEmail, demoMode, demoPersons, persons]);

  const dashboardAnalytics = demoMode ? organizationTreeDemoAnalytics : analytics;
  const displayedPersons = demoMode ? demoPersons : persons;
  const metrics = dashboardAnalytics?.basicMetrics || {};
  const rankDistribution = useMemo(
    () => dashboardAnalytics?.userMetrics?.rankDistribution || {},
    [dashboardAnalytics]
  );
  const categoryStats = dashboardAnalytics?.contentMetrics?.categoryStats || {};
  const dailyActivity = useMemo(
    () => dashboardAnalytics?.activityMetrics?.dailyActivity || [],
    [dashboardAnalytics]
  );
  const recentUsers = dashboardAnalytics?.detailedData?.recentUsers || [];
  const topVideos = dashboardAnalytics?.detailedData?.topVideos || [];
  const participationMetrics = dashboardAnalytics?.participationMetrics || {};
  const participationTimeline = participationMetrics.timeline || [];
  const recentParticipants = participationMetrics.recent || [];

  const maxDailyActivity = Math.max(
    1,
    ...dailyActivity.map((item) => item.activity || 0)
  );
  const maxRankCount = Math.max(1, ...Object.values(rankDistribution));
  const maxParticipationCount = Math.max(
    1,
    ...participationTimeline.map((item) => item.count || 0)
  );
  const participationPoints = participationTimeline
    .map((item, index) => {
      const x = participationTimeline.length > 1
        ? (index / (participationTimeline.length - 1)) * 660 + 20
        : 350;
      const y = 155 - ((item.count || 0) / maxParticipationCount) * 125;
      return `${x},${y}`;
    })
    .join(" ");
  const activityPoints = dailyActivity
    .map((item, index) => {
      const x = dailyActivity.length > 1
        ? (index / (dailyActivity.length - 1)) * 660 + 20
        : 350;
      const y = 155 - ((item.activity || 0) / maxDailyActivity) * 125;
      return `${x},${y}`;
    })
    .join(" ");
  const filteredParticipants = recentParticipants.filter((participant) =>
    String(participant.name || "")
      .toLocaleLowerCase("ro-RO")
      .includes(participantSearch.trim().toLocaleLowerCase("ro-RO"))
  );

  const statCards = [
    { label: "Utilizatori", value: metrics.totalUsers || 0, icon: Users },
    { label: "Activi în perioadă", value: metrics.activeUsers || 0, icon: Activity },
    { label: "Videoclipuri", value: metrics.totalVideos || 0, icon: Video },
    { label: "Vizionări bibliotecă", value: metrics.totalViews || 0, icon: BarChart3 },
    { label: "Conținut salvat", value: metrics.totalFavorites || 0, icon: BookOpen },
    { label: "Rată implicare", value: `${metrics.engagementRate || 0}%`, icon: TrendingUp },
    { label: "Membri noi", value: metrics.newUsers || 0, icon: UserCheck },
    { label: "Rată finalizare", value: `${metrics.completionRate || 0}%`, icon: Database },
  ];

  const exportDashboardReport = () => {
    const escapeCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["Secțiune", "Indicator", "Valoare", "Data"],
      ...statCards.map((card) => ["KPI", card.label, card.value, ""]),
      ...recentParticipants.map((participant) => [
        "Participări",
        participant.name || "Utilizator",
        1,
        participant.createdAt ? new Date(participant.createdAt).toLocaleString("ro-RO") : "",
      ]),
    ];
    const csv = `\uFEFF${rows.map((row) => row.map(escapeCell).join(",")).join("\n")}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `raport-dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="admin-insights__state" role="status">
        <RefreshCw className="admin-insights__spinner" />
        <span>Se pregătește tabloul de bord…</span>
      </div>
    );
  }

  return (
    <section className={`admin-insights admin-insights--${density}`}>
      <div className="admin-insights__toolbar">
        <div className="admin-insights__tabs" role="tablist" aria-label="Vizualizări dashboard">
          <button
            type="button"
            className={activeView === "overview" ? "active" : ""}
            onClick={() => setActiveView("overview")}
          >
            Analiză generală
          </button>
          <button
            type="button"
            className={activeView === "organization" ? "active" : ""}
            onClick={() => setActiveView("organization")}
          >
            Organizație
          </button>
        </div>

        <div className="admin-insights__actions">
          <button
            type="button"
            className={`admin-insights__demo-toggle ${demoMode ? "active" : ""}`}
            onClick={() => setDemoMode((current) => !current)}
            aria-pressed={demoMode}
          >
            <Database size={15} />
            {demoMode ? "Date demonstrative" : "Date reale"}
          </button>
          <label>
            <span>Perioadă</span>
            <select value={timeRange} onChange={(event) => setTimeRange(event.target.value)}>
              <option value="7d">7 zile</option>
              <option value="30d">30 zile</option>
              <option value="90d">90 zile</option>
              <option value="1y">1 an</option>
            </select>
          </label>
          <label>
            <span>Densitate</span>
            <select value={density} onChange={(event) => setDensity(event.target.value)}>
              <option value="comfortable">Aerisit</option>
              <option value="compact">Compact</option>
            </select>
          </label>
          <button type="button" className="admin-insights__export" onClick={exportDashboardReport}>
            <Download size={16} />
            Exportă raport
          </button>
          <button
            type="button"
            className="admin-insights__refresh"
            disabled={refreshing}
            onClick={() => {
              setRefreshing(true);
              loadDashboard({ quiet: true });
            }}
          >
            <RefreshCw size={16} className={refreshing ? "admin-insights__spinner" : ""} />
            Actualizează
          </button>
        </div>
      </div>

      {error && <div className="admin-insights__notice">{error}</div>}

      {activeView === "overview" ? (
        <>
          <div className="admin-insights__stats">
            {statCards.map(({ label, value, icon: Icon }) => (
              <article className="admin-insights__stat-card" key={label}>
                <span className="admin-insights__stat-icon"><Icon size={20} /></span>
                <strong>{typeof value === "number" ? value.toLocaleString("ro-RO") : value}</strong>
                <span>{label}</span>
              </article>
            ))}
          </div>

          <div className="admin-insights__grid">
            <article className="admin-insights__panel admin-insights__panel--wide">
              <header>
                <div>
                  <span>ACTIVITATE</span>
                  <h3>Interacțiuni zilnice</h3>
                </div>
                <div className="admin-insights__panel-tools">
                  <small>{dashboardAnalytics?.activityMetrics?.totalActivity || 0} total</small>
                  <div className="admin-insights__chart-switch" aria-label="Tip grafic activitate">
                    <button type="button" className={activityChartType === "bars" ? "active" : ""} onClick={() => setActivityChartType("bars")}>Bare</button>
                    <button type="button" className={activityChartType === "line" ? "active" : ""} onClick={() => setActivityChartType("line")}>Linie</button>
                  </div>
                </div>
              </header>
              {activityChartType === "bars" ? (
                <div className="admin-insights__chart" aria-label="Activitate în perioada selectată">
                  {dailyActivity.map((item) => (
                    <div className="admin-insights__chart-column" key={item.date}>
                      <span>{item.activity}</span>
                      <div
                        className="admin-insights__chart-bar"
                        style={{ height: `${Math.max(7, (item.activity / maxDailyActivity) * 100)}%` }}
                      />
                      <small>{new Date(item.date).toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" })}</small>
                    </div>
                  ))}
                  {dailyActivity.length === 0 && <p>Nu există activitate în perioada selectată.</p>}
                </div>
              ) : (
                <div className="admin-insights__line-chart" aria-label="Evoluția activității">
                  {dailyActivity.length > 0 ? <>
                    <svg viewBox="0 0 700 180" role="img" aria-label="Grafic liniar al activității">
                      <line x1="20" y1="155" x2="680" y2="155" className="admin-insights__line-axis" />
                      <polyline points={activityPoints} className="admin-insights__line-path" />
                      {dailyActivity.map((item, index) => {
                        const [x, y] = activityPoints.split(" ")[index].split(",");
                        return <g key={item.date}>
                          <circle cx={x} cy={y} r="5" className="admin-insights__line-point" />
                          <text x={x} y={Number(y) - 12} textAnchor="middle">{item.activity}</text>
                        </g>;
                      })}
                    </svg>
                    <div className="admin-insights__line-labels" style={{ gridTemplateColumns: `repeat(${dailyActivity.length}, minmax(0, 1fr))` }}>
                      {dailyActivity.map((item) => <span key={item.date}>{new Date(item.date).toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" })}</span>)}
                    </div>
                  </> : <p className="admin-insights__empty">Nu există activitate în perioada selectată.</p>}
                </div>
              )}
            </article>

            <article className="admin-insights__panel admin-insights__panel--wide admin-insights__participation">
              <header>
                <div>
                  <span>PARTICIPĂRI</span>
                  <h3>Sesiuni de masaj în palmă</h3>
                </div>
                <div className="admin-insights__panel-tools">
                  <small>{participationMetrics.periodTotal || 0} în perioada selectată · {participationMetrics.total || 0} total</small>
                  <label className="admin-insights__participant-search">
                    <Search size={15} aria-hidden="true" />
                    <input value={participantSearch} onChange={(event) => setParticipantSearch(event.target.value)} placeholder="Caută participant" />
                  </label>
                </div>
              </header>

              <div className="admin-insights__line-chart" aria-label="Participări în perioada selectată">
                {participationTimeline.length > 0 ? (
                  <>
                    <svg viewBox="0 0 700 180" role="img" aria-label="Evoluția numărului de participări">
                      <line x1="20" y1="155" x2="680" y2="155" className="admin-insights__line-axis" />
                      <polyline points={participationPoints} className="admin-insights__line-path" />
                      {participationTimeline.map((item, index) => {
                        const [x, y] = participationPoints.split(" ")[index].split(",");
                        return (
                          <g key={item.date}>
                            <circle cx={x} cy={y} r="5" className="admin-insights__line-point" />
                            <text x={x} y={Number(y) - 12} textAnchor="middle">{item.count}</text>
                          </g>
                        );
                      })}
                    </svg>
                    <div className="admin-insights__line-labels">
                      {participationTimeline.map((item) => (
                        <span key={item.date}>{new Date(`${item.date}T12:00:00`).toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" })}</span>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="admin-insights__empty">Nu există participări în perioada selectată.</p>
                )}
              </div>

              <div className="admin-insights__table-wrap admin-insights__participants-table">
                <table>
                  <thead>
                    <tr><th>Nume</th><th>Data înscrierii</th></tr>
                  </thead>
                  <tbody>
                    {filteredParticipants.map((participant) => (
                      <tr key={participant.id}>
                        <td>{participant.name}</td>
                        <td>{new Date(participant.createdAt).toLocaleString("ro-RO", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredParticipants.length === 0 && <p className="admin-insights__empty">Nu există înscrieri pentru această căutare.</p>}
              </div>
            </article>

            <article className="admin-insights__panel">
              <header>
                <div>
                  <span>COMUNITATE</span>
                  <h3>Distribuție rank</h3>
                </div>
              </header>
              <div className="admin-insights__rank-list">
                {Object.entries(rankDistribution).map(([rank, count]) => (
                  <div key={rank}>
                    <div><span>{getRankLabel(rank)}</span><strong>{count}</strong></div>
                    <span className="admin-insights__progress">
                      <i style={{
                        width: `${(count / maxRankCount) * 100}%`,
                        backgroundColor: getRankColor(rank),
                      }} />
                    </span>
                  </div>
                ))}
                {Object.keys(rankDistribution).length === 0 && <p>Nu există date despre rank.</p>}
              </div>
            </article>

            <article className="admin-insights__panel">
              <header>
                <div>
                  <span>PERFORMANȚĂ</span>
                  <h3>Materiale apreciate</h3>
                </div>
              </header>
              <div className="admin-insights__top-content">
                {topVideos.slice(0, 5).map((video, index) => (
                  <div key={video.id || video.title}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <div>
                      <strong>{video.title || "Material fără titlu"}</strong>
                      <small>{video.category || "General"}</small>
                    </div>
                    <b>{Number(video.viewCount || 0).toLocaleString("ro-RO")}</b>
                  </div>
                ))}
                {topVideos.length === 0 && <p>Nu există date despre materiale încă.</p>}
              </div>
            </article>

            <article className="admin-insights__panel">
              <header>
                <div>
                  <span>REȚEA</span>
                  <h3>Sănătatea comunității</h3>
                </div>
              </header>
              <div className="admin-insights__health-grid">
                <div className="admin-insights__health-ring" style={{ '--health-value': `${Math.min(100, Number(metrics.engagementRate || 0)) * 3.6}deg` }}>
                  <strong>{metrics.engagementRate || 0}%</strong>
                  <span>implicare</span>
                </div>
                <div className="admin-insights__health-ring" style={{ '--health-value': `${Math.min(100, Number(metrics.completionRate || 0)) * 3.6}deg` }}>
                  <strong>{metrics.completionRate || 0}%</strong>
                  <span>finalizare</span>
                </div>
                <div>
                  <strong>{metrics.totalWatchLater || 0}</strong>
                  <span>salvate ulterior</span>
                </div>
                <div>
                  <strong>{displayedPersons.filter((person) => person.assignedTo && person.assignedTo !== "None").length}</strong>
                  <span>relații mentor</span>
                </div>
              </div>
            </article>

            <article className="admin-insights__panel">
              <header>
                <div>
                  <span>BIBLIOTECĂ</span>
                  <h3>Conținut pe categorii</h3>
                </div>
              </header>
              <div className="admin-insights__category-list">
                {Object.entries(categoryStats).slice(0, 8).map(([category, data]) => (
                  <div key={category}>
                    <span>{category}</span>
                    <strong>{data.count} materiale</strong>
                    <small>{data.views || 0} vizionări</small>
                  </div>
                ))}
                {Object.keys(categoryStats).length === 0 && <p>Nu există categorii încă.</p>}
              </div>
            </article>

            <article className="admin-insights__panel admin-insights__panel--wide">
              <header>
                <div>
                  <span>MEMBRI NOI</span>
                  <h3>Înregistrări recente</h3>
                </div>
              </header>
              <div className="admin-insights__table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Utilizator</th>
                      <th>Email</th>
                      <th>Rank</th>
                      <th>Data înscrierii</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentUsers.map((person) => (
                      <tr key={person.id}>
                        <td>{person.displayName || "Utilizator"}</td>
                        <td>{person.email || "—"}</td>
                        <td>
                          <span className="admin-insights__badge" style={getRankStyle(person.rank)}>
                            {getRankLabel(person.rank)}
                          </span>
                        </td>
                        <td>{person.createdAt?._seconds
                          ? new Date(person.createdAt._seconds * 1000).toLocaleDateString("ro-RO")
                          : person.createdAt
                            ? new Date(person.createdAt).toLocaleDateString("ro-RO")
                            : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {recentUsers.length === 0 && <p className="admin-insights__empty">Nu există înregistrări recente.</p>}
              </div>
            </article>
          </div>
        </>
      ) : (
        <div className="admin-insights__organization">
          <div className="admin-insights__organization-summary">
            <div><strong>{displayedPersons.length}</strong><span>persoane în rețea</span></div>
            <div><strong>{goals.length}</strong><span>obiective configurate</span></div>
            <div><strong>{new Set(displayedPersons.map((person) => person.type).filter(Boolean)).size}</strong><span>roluri active</span></div>
          </div>
          {demoMode && (
            <div className="admin-insights__demo-note">
              Set vizual cu 20 de persoane. Modificările prin drag-and-drop sunt păstrate local în această sesiune.
            </div>
          )}
          <PersonTree persons={displayedPersons} updatePerson={updatePerson} />
        </div>
      )}
    </section>
  );
};

export default MyDashboard;
